import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import AppHeader from "@/components/app/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Activity, RefreshCw, Gauge, AlertTriangle, Users, Server, BellRing, HeartPulse } from "lucide-react";


function fmtMs(v) {
  if (v === null || v === undefined) return "—";
  return v < 10 ? `${v.toFixed(1)} ms` : `${Math.round(v)} ms`;
}
function fmtRate(v) {
  return `${(v * 100).toFixed(2)}%`;
}
function fmtTs(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
}


function BigStat({ label, value, sub, tone = "neutral", testid }) {
  const toneCls = {
    neutral: "border-foreground/20",
    good:    "border-emerald-500/40 bg-emerald-50",
    warn:    "border-amber-500/40 bg-amber-50",
    bad:     "border-rose-500/40 bg-rose-50",
  }[tone] || "border-foreground/20";
  return (
    <div className={`border ${toneCls} p-5`} data-testid={testid}>
      <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className="font-semibold tracking-tight text-3xl mt-2">{value}</div>
      {sub && <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}


export default function AdminMetrics() {
  const { user, loading } = useAuth();
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = async (h = hours) => {
    setBusy(true);
    try {
      const [{ data: m }, { data: a }, { data: hh }] = await Promise.all([
        api.get(`/admin/metrics?hours=${h}`),
        api.get(`/admin/alerts?limit=10`),
        api.get(`/health/full`),
      ]);
      setData(m);
      setAlerts(a || []);
      setHealth(hh);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load metrics");
    } finally {
      setBusy(false);
    }
  };
  const runCheck = async () => {
    setChecking(true);
    try {
      await api.post(`/admin/alerts/check`);
      toast.success("Alert check ran — see history below");
      await load(hours);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Check failed");
    } finally {
      setChecking(false);
    }
  };
  useEffect(() => { if (user?.role === "admin") load(hours); }, [user, hours]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;

  const sb = data?.status_breakdown || {};
  const totalSb = (sb["2xx"] || 0) + (sb["3xx"] || 0) + (sb["4xx"] || 0) + (sb["5xx"] || 0);
  const errRate = data?.error_rate ?? 0;
  const errTone = errRate > 0.01 ? "bad" : errRate > 0 ? "warn" : "good";

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 space-y-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">// admin / observability</div>
            <h1 className="font-semibold tracking-tight text-4xl sm:text-5xl mt-2">
              Metrics.
            </h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Derived from structured request logs. Latency percentiles, top paths, and slowest
              endpoints across the past {data?.window_hours ?? hours} hour(s).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(hours)} onValueChange={(v) => setHours(parseInt(v))}>
              <SelectTrigger className="w-[140px] rounded-none" data-testid="metrics-window-select">
                <SelectValue placeholder="Window" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 1h</SelectItem>
                <SelectItem value="6">Last 6h</SelectItem>
                <SelectItem value="24">Last 24h</SelectItem>
                <SelectItem value="168">Last 7d</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              className="rounded-none h-10"
              onClick={runCheck}
              disabled={checking}
              data-testid="metrics-run-check"
            >
              <BellRing className={`w-4 h-4 mr-1.5 ${checking ? "animate-pulse" : ""}`} />
              {checking ? "Checking…" : "Run alert check"}
            </Button>
            <Button
              variant="outline"
              className="rounded-none h-10"
              onClick={() => load(hours)}
              disabled={busy}
              data-testid="metrics-refresh"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Health strip */}
        {health && (
          <div className="border border-foreground/15 bg-white px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-2"
               data-testid="health-strip">
            <div className="flex items-center gap-2">
              <HeartPulse
                className={`w-4 h-4 ${health.status === "ok" ? "text-emerald-600" : "text-rose-600"}`}
                strokeWidth={2.5}
              />
              <span className={`font-mono-data text-[11px] uppercase tracking-[0.18em] font-semibold ${
                health.status === "ok" ? "text-emerald-700" : "text-rose-700"
              }`}>
                {health.status === "ok" ? "all systems go" : "degraded"}
              </span>
            </div>
            {Object.entries(health.components || {}).map(([name, c]) => {
              const ok = ["ok", "running", "configured"].includes(c.status);
              const dim = c.status === "not_configured";
              return (
                <div key={name} className="flex items-center gap-1.5 font-mono-data text-[11px]" data-testid={`health-component-${name}`}>
                  <span className={`inline-block w-2 h-2 rounded-full ${
                    ok ? "bg-emerald-500" : dim ? "bg-zinc-300" : "bg-rose-500"
                  }`} />
                  <span className="uppercase tracking-widest text-muted-foreground">{name}:</span>
                  <span className={ok ? "text-foreground" : dim ? "text-muted-foreground" : "text-rose-700"}>
                    {c.status}{c.mode ? ` (${c.mode})` : ""}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Top stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <BigStat label="REQUESTS" value={(data?.total_requests ?? 0).toLocaleString()}
                   sub={`${data?.requests_per_min ?? 0} / min`} testid="stat-requests" />
          <BigStat label="P50 LATENCY" value={fmtMs(data?.p50_ms)} testid="stat-p50" />
          <BigStat label="P95 LATENCY" value={fmtMs(data?.p95_ms)}
                   tone={(data?.p95_ms ?? 0) > 800 ? "warn" : "neutral"}
                   sub={`p99: ${fmtMs(data?.p99_ms)}`} testid="stat-p95" />
          <BigStat label="ERROR RATE" value={fmtRate(errRate)}
                   tone={errTone}
                   sub={`4xx: ${fmtRate(data?.rate_4xx ?? 0)}`} testid="stat-errors" />
        </div>

        {/* Status breakdown */}
        <div className="border border-foreground/15 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Server className="w-4 h-4" />
            <div className="font-mono-data text-[11px] uppercase tracking-[0.18em]">Status breakdown</div>
          </div>
          {totalSb === 0 ? (
            <div className="text-sm text-muted-foreground">No data in this window yet.</div>
          ) : (
            <>
              <div className="flex h-2.5 overflow-hidden" data-testid="status-bar">
                {[
                  ["2xx", "bg-emerald-500"],
                  ["3xx", "bg-sky-500"],
                  ["4xx", "bg-amber-500"],
                  ["5xx", "bg-rose-500"],
                ].map(([k, cls]) => {
                  const w = totalSb ? (sb[k] || 0) / totalSb * 100 : 0;
                  return <div key={k} className={cls} style={{ width: `${w}%` }} />;
                })}
              </div>
              <div className="grid grid-cols-4 gap-3 mt-4 font-mono-data text-xs">
                {["2xx", "3xx", "4xx", "5xx"].map((k) => (
                  <div key={k}>
                    <div className="uppercase tracking-widest text-muted-foreground">{k}</div>
                    <div className="text-foreground text-lg mt-0.5">{(sb[k] || 0).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Top paths */}
          <div className="border border-foreground/15 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-4 h-4" />
              <div className="font-mono-data text-[11px] uppercase tracking-[0.18em]">Top paths by volume</div>
            </div>
            {(data?.top_paths || []).length === 0 ? (
              <div className="text-sm text-muted-foreground">No data.</div>
            ) : (
              <table className="w-full font-mono-data text-xs" data-testid="table-top-paths">
                <thead>
                  <tr className="text-muted-foreground uppercase tracking-widest text-[10px]">
                    <th className="text-left py-1.5">Path</th>
                    <th className="text-right py-1.5">Count</th>
                    <th className="text-right py-1.5">p50</th>
                    <th className="text-right py-1.5">p95</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.top_paths || []).map((p) => (
                    <tr key={p.path} className="border-t border-dashed border-foreground/10">
                      <td className="py-1.5 truncate max-w-[260px]">{p.path}</td>
                      <td className="py-1.5 text-right">{p.count}</td>
                      <td className="py-1.5 text-right">{fmtMs(p.p50_ms)}</td>
                      <td className="py-1.5 text-right">{fmtMs(p.p95_ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Slowest paths */}
          <div className="border border-foreground/15 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Gauge className="w-4 h-4" />
              <div className="font-mono-data text-[11px] uppercase tracking-[0.18em]">Slowest paths (p95)</div>
            </div>
            {(data?.slowest_paths || []).length === 0 ? (
              <div className="text-sm text-muted-foreground">No data (≥ 3 samples required).</div>
            ) : (
              <table className="w-full font-mono-data text-xs" data-testid="table-slowest-paths">
                <thead>
                  <tr className="text-muted-foreground uppercase tracking-widest text-[10px]">
                    <th className="text-left py-1.5">Path</th>
                    <th className="text-right py-1.5">Samples</th>
                    <th className="text-right py-1.5">p50</th>
                    <th className="text-right py-1.5">p95</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.slowest_paths || []).map((p) => (
                    <tr key={p.path} className="border-t border-dashed border-foreground/10">
                      <td className="py-1.5 truncate max-w-[260px]">{p.path}</td>
                      <td className="py-1.5 text-right">{p.count}</td>
                      <td className="py-1.5 text-right">{fmtMs(p.p50_ms)}</td>
                      <td className="py-1.5 text-right text-rose-700">{fmtMs(p.p95_ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Alert history */}
        <div className="border border-foreground/15 p-6" data-testid="alerts-panel">
          <div className="flex items-center gap-2 mb-4">
            <BellRing className="w-4 h-4" />
            <div className="font-mono-data text-[11px] uppercase tracking-[0.18em]">Recent alerts</div>
          </div>
          {alerts.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No alerts fired yet. Alerts trigger when p95 latency &gt; 1000ms or 5xx error rate &gt; 1% over a 15-minute window (with a 60-minute cooldown per condition).
            </div>
          ) : (
            <table className="w-full font-mono-data text-xs" data-testid="table-alerts">
              <thead>
                <tr className="text-muted-foreground uppercase tracking-widest text-[10px]">
                  <th className="text-left py-1.5">Fired</th>
                  <th className="text-left py-1.5">Kind</th>
                  <th className="text-left py-1.5">Observed</th>
                  <th className="text-left py-1.5">Threshold</th>
                  <th className="text-right py-1.5">Email</th>
                  <th className="text-right py-1.5">Slack</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((a) => (
                  <tr key={a.id} className="border-t border-dashed border-foreground/10">
                    <td className="py-1.5">{fmtTs(a.fired_at)}</td>
                    <td className="py-1.5">
                      <span className={
                        a.kind === "error_rate"
                          ? "bg-rose-50 text-rose-700 border border-rose-200 px-1.5 py-0.5"
                          : "bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5"
                      }>
                        {a.kind}
                      </span>
                    </td>
                    <td className="py-1.5">{a.value}</td>
                    <td className="py-1.5 text-muted-foreground">{a.threshold}</td>
                    <td className="py-1.5 text-right">{a.delivered} / {(a.recipients || []).length}</td>
                    <td className="py-1.5 text-right">
                      {a.slack_delivered
                        ? <span className="text-emerald-700">✓ posted</span>
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Top users */}
        <div className="border border-foreground/15 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4" />
            <div className="font-mono-data text-[11px] uppercase tracking-[0.18em]">Top users by request count</div>
          </div>
          {(data?.top_users || []).length === 0 ? (
            <div className="text-sm text-muted-foreground">No authenticated traffic in this window.</div>
          ) : (
            <table className="w-full font-mono-data text-xs" data-testid="table-top-users">
              <thead>
                <tr className="text-muted-foreground uppercase tracking-widest text-[10px]">
                  <th className="text-left py-1.5">User ID</th>
                  <th className="text-right py-1.5">Requests</th>
                </tr>
              </thead>
              <tbody>
                {(data?.top_users || []).map((u) => (
                  <tr key={u.user_id} className="border-t border-dashed border-foreground/10">
                    <td className="py-1.5">{u.user_id}</td>
                    <td className="py-1.5 text-right">{u.requests}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="text-center font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground pb-6">
          <AlertTriangle className="w-3 h-3 inline mr-1 -translate-y-0.5" />
          Derived from /var/log/supervisor/backend.err.log. Restarts truncate older windows.{" "}
          <Link to="/app" className="underline">← Back to dashboard</Link>
        </div>
      </main>
    </div>
  );
}
